<script lang="ts">
  import { createEventDispatcher, onMount, onDestroy } from 'svelte';
  import { push } from 'svelte-spa-router';
  import { api } from '../../framework/js/api.js';
  import { toast } from '../../framework/js/toast.js';
  import BriefBuilderSidebar from './BriefBuilderSidebar.svelte';
  import {
    TERRITORIES,
    TARGET_SECONDS,
    buildSteps,
    midCheckNarrative,
    territoryTitle,
  } from './questions';
  import {
    emptyAnswers,
    practicalitiesSummary,
    type BriefBuilderAnswers,
    type TerritoryId,
    type WizardPhase,
  } from './types';
  import { fromApiAnswers, toApiAnswers } from './answers';
  import { sanitizeHtml } from '../../framework/js/sanitizeHtml';

  export let briefId: string | null = null;
  /** When true, skip API create/save and use localStorage (guest). */
  export let guest = false;
  export let projectName = 'Untitled brief';

  const dispatch = createEventDispatcher();

  let answers: BriefBuilderAnswers = emptyAnswers();
  let phase: WizardPhase = 'intro';
  let stepIndex = 0;
  let draft = '';
  let elapsedSeconds = 0;
  let timerId: ReturnType<typeof setInterval> | null = null;
  let saving = false;
  let creating = false;
  let streamText = '';
  let streamError = '';
  let localBriefId = briefId;
  /** True while loading an existing brief for continue/rework. */
  let loadingBrief = Boolean(briefId) && !guest;
  /**
   * Rework of a completed version (BriefView sets draftBaseVersion_*).
   * Hides "mark as unknown" and creates a draft version on entry.
   */
  let isRework = false;

  /** AI mid-check result; falls back to local template when null. */
  let midAi: { text: string; highlights: string[]; flags: string[] } | null = null;
  let midLoading = false;
  let midError = '';

  $: steps = buildSteps(answers);
  $: currentStep = steps[stepIndex];
  $: liveDraft =
    currentStep?.kind === 'text'
      ? draft
      : currentStep?.kind === 'territory'
        ? answers.territoryLabel
        : currentStep?.kind === 'choice'
          ? [answers.ideas, answers.ideasDetail].filter(Boolean).join(' — ')
          : currentStep?.kind === 'when' || currentStep?.kind === 'practicalities'
            ? practicalitiesSummary(answers.practicalities)
            : '';

  function startTimer() {
    if (timerId) return;
    timerId = setInterval(() => {
      elapsedSeconds += 1;
    }, 1000);
  }

  function stopTimer() {
    if (timerId) {
      clearInterval(timerId);
      timerId = null;
    }
  }

  onMount(async () => {
    if (guest) {
      try {
        const raw = localStorage.getItem('guestBriefBuilderAnswers');
        if (raw) answers = fromApiAnswers(JSON.parse(raw));
        const step = localStorage.getItem('guestBriefBuilderStep');
        if (step === 'intro' || !step) phase = 'intro';
        else if (step === 'mid_check') phase = 'mid_check';
        else if (step === 'final_readback') phase = 'final_readback';
        else {
          phase = 'question';
          stepIndex = Math.min(parseInt(step, 10) || 0, steps.length - 1);
          hydrateDraft();
        }
      } catch {
        /* ignore */
      }
      return;
    }

    if (localBriefId) {
      loadingBrief = true;
      try {
        const baseVersionStr = localStorage.getItem(`draftBaseVersion_${localBriefId}`);
        const baseVersion = baseVersionStr ? parseInt(baseVersionStr, 10) : null;
        isRework = Number.isFinite(baseVersion);

        const res: any = await api.getBriefBuilder(localBriefId);
        if (res?.success && res.data?.brief) {
          const brief = res.data.brief;
          projectName = brief.name || projectName;
          if (brief.builder_answers) {
            answers = fromApiAnswers(brief.builder_answers);
          }

          // Create draft version as soon as rework starts (matches legacy BtoB).
          // The draft becomes a completed version when generation finishes.
          if (isRework) {
            try {
              await api.createDraftVersion(localBriefId, baseVersion);
            } catch (e) {
              console.error('[BriefBuilder] Could not create draft version', e);
            }
            localStorage.removeItem(`draftBaseVersion_${localBriefId}`);
          }

          // Continue / rework always reopen at question 01 with answers prefilled
          // so you can walk through and edit — not jump to the final confirm.
          phase = 'question';
          stepIndex = 0;
          hydrateDraft();
          startTimer();
        } else {
          toast.showNotification(false, res?.message || 'Could not load brief');
        }
      } catch {
        toast.showNotification(false, 'Could not load brief');
      } finally {
        loadingBrief = false;
      }
    }
  });

  onDestroy(stopTimer);

  function persistGuest() {
    if (!guest) return;
    localStorage.setItem('guestBriefBuilderAnswers', JSON.stringify(answers));
    if (phase === 'question') {
      localStorage.setItem('guestBriefBuilderStep', String(stepIndex));
    } else {
      localStorage.setItem('guestBriefBuilderStep', phase);
    }
  }

  async function persistAnswers() {
    persistGuest();
    if (guest || !localBriefId) return;
    try {
      await api.saveBriefBuilderAnswers(localBriefId, toApiAnswers(answers));
    } catch {
      /* non-blocking */
    }
  }

  function hydrateDraft() {
    const s = steps[stepIndex];
    if (!s || s.kind !== 'text') {
      draft = '';
      return;
    }
    const key = s.answerKey;
    if (key === 'practicalities' || key === 'unknowns') {
      draft = '';
      return;
    }
    const value = answers[key];
    draft = typeof value === 'string' ? value : '';
  }

  function applyDraftToAnswers() {
    const s = currentStep;
    if (!s || s.kind !== 'text') return;
    const v = draft.trim();
    switch (s.id) {
      case 'decision':
        answers.decision = v;
        break;
      case 'honest_question':
        answers.honestQuestion = v;
        break;
      case 'people':
        answers.people = v;
        break;
      case 'what_you_know':
        answers.whatYouKnow = v;
        break;
      case 'insider_fact':
        answers.insiderFact = v;
        break;
      case 'territory_q1':
        answers.territoryQ1 = v;
        break;
      case 'brand_impact':
        answers.brandImpact = v;
        break;
      case 'territory_q2':
        answers.territoryQ2 = v;
        break;
      case 'optional':
        answers.optional = v;
        break;
    }
    answers = answers;
  }

  function markUnknown() {
    const id = currentStep?.id;
    if (!id) return;
    if (!answers.unknowns.includes(id)) {
      answers.unknowns = [...answers.unknowns, id];
    }
    draft = '';
    applyDraftToAnswers();
    goNext();
  }

  function clearUnknown(id: string) {
    answers.unknowns = answers.unknowns.filter((u) => u !== id);
  }

  async function ensureBriefCreated(): Promise<boolean> {
    if (guest || localBriefId) return true;
    if (creating) return false;
    creating = true;
    try {
      const name =
        answers.decision.slice(0, 80) ||
        projectName ||
        'New research brief';
      const res: any = await api.createBriefBuilder(name);
      if (!res?.success || !res?.data?.brief?.uuid) {
        toast.showNotification(false, res?.message || 'Could not create brief');
        return false;
      }
      localBriefId = res.data.brief.uuid;
      return true;
    } catch {
      toast.showNotification(false, 'Could not create brief');
      return false;
    } finally {
      creating = false;
    }
  }

  async function begin() {
    phase = 'question';
    stepIndex = 0;
    hydrateDraft();
    startTimer();
    persistGuest();
  }

  async function goNext() {
    applyDraftToAnswers();
    if (currentStep && draft.trim()) clearUnknown(currentStep.id);

    if (currentStep?.kind === 'territory' && !answers.territory) {
      toast.showNotification(false, 'Pick the closest one to continue');
      return;
    }
    if (currentStep?.kind === 'choice' && !choiceReady) {
      toast.showNotification(false, 'Pick one to continue');
      return;
    }
    if (currentStep?.kind === 'when' && !answers.practicalities.decisionDate.trim()) {
      toast.showNotification(false, 'Say when you need an answer, and who needs to see it');
      return;
    }
    if (
      currentStep?.kind === 'text' &&
      !currentStep.optional &&
      !draft.trim() &&
      !answers.unknowns.includes(currentStep.id)
    ) {
      toast.showNotification(false, 'Add an answer, or mark as unknown');
      return;
    }

    await ensureBriefCreated();
    await persistAnswers();

    const peopleIndex = steps.findIndex((s) => s.id === 'people');
    if (phase === 'question' && stepIndex === peopleIndex) {
      phase = 'mid_check';
      persistGuest();
      void runMidCheck();
      return;
    }

    if (stepIndex >= steps.length - 1) {
      phase = 'final_readback';
      persistGuest();
      return;
    }

    stepIndex += 1;
    hydrateDraft();
    persistGuest();
  }

  function goBack() {
    applyDraftToAnswers();
    if (phase === 'mid_check') {
      phase = 'question';
      stepIndex = Math.max(0, steps.findIndex((s) => s.id === 'people'));
      hydrateDraft();
      return;
    }
    if (phase === 'final_readback') {
      phase = 'question';
      stepIndex = steps.length - 1;
      hydrateDraft();
      return;
    }
    if (stepIndex > 0) {
      stepIndex -= 1;
      hydrateDraft();
    }
  }

  function continueAfterMidCheck() {
    phase = 'question';
    const peopleIndex = steps.findIndex((s) => s.id === 'people');
    stepIndex = peopleIndex < 0 ? 0 : Math.min(steps.length - 1, peopleIndex + 1);
    hydrateDraft();
    persistGuest();
  }

  function selectTerritory(id: TerritoryId) {
    const t = TERRITORIES.find((x) => x.id === id);
    answers.territory = id;
    answers.territoryLabel = t?.title || territoryTitle(id);
    answers = answers;
    clearUnknown('territory');
  }

  // Must be a reactive declaration — calling a function in the template
  // does not re-run when `draft` changes (Svelte 4 dependency tracking).
  const evidenceOptions: { id: BriefBuilderAnswers['practicalities']['solidity']; title: string; hint: string }[] = [
    { id: 'directional', title: 'Directional', hint: 'Point the way' },
    { id: 'decision_grade', title: 'Decision-grade', hint: 'Team can commit' },
    { id: 'board_grade', title: 'Board-grade', hint: 'Survives the CFO' },
  ];
  $: canContinueText = currentStep?.optional === true || draft.trim().length > 0;
  $: choiceReady =
    answers.ideas.trim().length > 0 &&
    (answers.ideas !== 'I have a few ideas' || answers.ideasDetail.trim().length > 0);

  function toggleEvidence(id: BriefBuilderAnswers['practicalities']['solidity']) {
    answers.practicalities.solidity = answers.practicalities.solidity === id ? '' : id;
    answers = answers;
  }

  function skipOptional() {
    draft = '';
    applyDraftToAnswers();
    void goNext();
  }

  async function startGeneration() {
    if (guest) {
      localStorage.setItem('guestBriefBuilderAnswers', JSON.stringify(answers));
      localStorage.setItem('guestTrialProduct', 'btob');
      toast.showNotification(true, 'Create an account or log in to generate your brief');
      push('/signup');
      return;
    }

    const ok = await ensureBriefCreated();
    if (!ok || !localBriefId) return;
    await persistAnswers();

    phase = 'generating';
    streamText = '';
    streamError = '';
    stopTimer();

    try {
      await api.streamBriefBuilder(localBriefId, toApiAnswers(answers), {
        onToken: (t: string) => {
          streamText += t;
        },
        onDone: () => {
          phase = 'done';
          push(`/brief-builder/${localBriefId}`);
        },
        onError: (msg: string) => {
          streamError = msg || 'Generation failed';
          phase = 'final_readback';
          toast.showNotification(false, streamError);
        },
      });
    } catch (e: any) {
      streamError = e?.message || 'Generation failed';
      phase = 'final_readback';
      toast.showNotification(false, streamError);
    }
  }

  $: midFallback = midCheckNarrative(answers);
  $: mid = midAi ?? midFallback;

  async function runMidCheck() {
    midAi = null;
    midError = '';
    midLoading = true;

    // Guests: keep improved local template (no auth for AI mid-check yet).
    if (guest || !localBriefId) {
      midLoading = false;
      return;
    }

    try {
      const res: any = await api.midCheckBriefBuilder(localBriefId, toApiAnswers(answers));
      if (res?.success && res?.data?.narrative) {
        midAi = {
          text: String(res.data.narrative),
          highlights: Array.isArray(res.data.highlights)
            ? res.data.highlights.map(String)
            : [],
          flags: Array.isArray(res.data.flags) ? res.data.flags.map(String) : [],
        };
      } else {
        midError = res?.message || 'Could not run AI check — showing a draft read-back.';
      }
    } catch {
      midError = 'Could not run AI check — showing a draft read-back.';
    } finally {
      midLoading = false;
    }
  }

  function highlightNarrative(text: string, highlights: string[]): string {
    let out = text;
    for (const h of highlights) {
      if (!h || h.length < 3) continue;
      const esc = h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      out = out.replace(new RegExp(esc, 'g'), `<mark>${h}</mark>`);
    }
    return out;
  }

  function readbackRows() {
    return [
      { label: 'The decision', value: answers.decision },
      { label: 'The trigger', value: answers.territoryQ1 },
      { label: 'The brand', value: answers.brandImpact },
      { label: 'The territory', value: answers.territoryLabel },
      { label: 'Competitors', value: answers.insiderFact },
      { label: 'Gut feeling', value: [answers.honestQuestion, answers.leastSure].filter(Boolean).join(' — ') },
      { label: 'Who to talk to', value: answers.people },
      { label: 'Already looked at', value: [answers.whatYouKnow, answers.dataHeld].filter(Boolean).join(' — ') },
      { label: 'Ideas', value: [answers.ideas, answers.ideasDetail].filter(Boolean).join(' — ') },
      { label: 'When and who', value: practicalitiesSummary(answers.practicalities) },
      { label: 'Anything else', value: answers.optional },
    ].filter((r) => r.value || answers.unknowns.length);
  }

  function handleExit() {
    if (guest) {
      push('/guest-brief-new');
      return;
    }
    dispatch('close');
  }
</script>

<div
  class="bb-shell"
  class:with-sidebar={phase !== 'intro' && phase !== 'generating' && phase !== 'done'}
  class:is-intro={phase === 'intro'}
  class:is-generating={phase === 'generating'}
>
  <header class="bb-topbar">
    <button type="button" class="bb-exit" on:click={handleExit} aria-label={guest ? 'Back' : 'Exit consultation'}>
      ← {guest ? 'Back' : 'Exit'}
    </button>
    {#if phase !== 'intro' && phase !== 'done'}
      <div class="bb-timer" class:over={elapsedSeconds > TARGET_SECONDS}>
        {Math.floor(elapsedSeconds / 60)}:{(elapsedSeconds % 60).toString().padStart(2, '0')} elapsed · target &lt; 10:00
      </div>
    {:else}
      <span class="bb-topbar-spacer" aria-hidden="true"></span>
    {/if}
  </header>

  {#if loadingBrief}
    <div class="bb-stage bb-stage-center" role="status" aria-live="polite">
      <div class="bb-intro">
        <p class="bb-eyebrow">Built-to-Brief</p>
        <h1 class="bb-intro-title">Loading your answers…</h1>
        <p class="bb-intro-body">Restoring your consultation so you can pick up where you left off.</p>
      </div>
    </div>
  {:else if phase === 'intro'}
    <div class="bb-stage bb-stage-center">
      <div class="bb-intro">
        <p class="bb-eyebrow">A ten-minute consultation</p>
        <h1 class="bb-intro-title">
          Eleven questions. One <em>decision-ready</em> research brief.
        </h1>
        <p class="bb-intro-body">
          Answer the way you'd talk to a researcher across the table — in sentences, with real examples.
          The better you think here, the sharper your brief.
        </p>
        <blockquote class="bb-tip">
          “I don't know” is always a legitimate answer. Unknowns don't weaken your brief — they become
          the questions your research answers.
        </blockquote>
        <div class="bb-metrics" role="list">
          <div class="bb-metric" role="listitem">
            <span class="bb-metric-num">11</span>
            <span class="bb-metric-label">Questions</span>
          </div>
          <div class="bb-metric" role="listitem">
            <span class="bb-metric-num">&lt;10</span>
            <span class="bb-metric-label">Minutes</span>
          </div>
          <div class="bb-metric" role="listitem">
            <span class="bb-metric-num">1</span>
            <span class="bb-metric-label">Complete brief</span>
          </div>
        </div>
        <button type="button" class="bb-primary" on:click={begin}>Begin</button>
      </div>
    </div>
  {:else if phase === 'generating'}
    <div class="bb-stage">
      <div class="bb-generating">
        <div class="bb-wait-dots" aria-hidden="true"><span></span><span></span><span></span></div>
        <h1 class="bb-q-title">Give me a moment — connecting what you've told me...</h1>
        <p class="bb-q-hint">
          An experienced researcher is reading through your answers and working out what actually matters.
        </p>
      </div>
    </div>
  {:else}
    <div class="bb-body">
      <main class="bb-main">
        <div class="bb-main-inner">
          {#if phase === 'mid_check'}
            <p class="bb-eyebrow">A moment to check</p>
            <div class="bb-readback-card">
              <p class="bb-readback-label">
                {midLoading ? 'Thinking…' : 'Reading it back to you'}
              </p>
              {#if midLoading}
                <p class="bb-readback-text bb-readback-loading">
                  Thinking through your idea so I can confirm I’ve understood you…
                </p>
              {:else}
                <p class="bb-readback-text">
                  {@html sanitizeHtml(highlightNarrative(mid.text, mid.highlights))}
                </p>
              {/if}
            </div>
            {#if !midLoading && mid.flags?.length}
              <ul class="bb-mid-flags" aria-label="Things I noticed">
                {#each mid.flags as flag (flag)}
                  <li>
                    <i class="exclamation circle icon" aria-hidden="true"></i>
                    <span>{flag}</span>
                  </li>
                {/each}
              </ul>
            {/if}
            {#if midError && !midLoading}
              <p class="bb-mid-note" role="status">{midError}</p>
            {/if}
            <h2 class="bb-q-title">Have I understood you correctly so far?</h2>
            <div class="bb-actions">
              <button
                type="button"
                class="bb-primary"
                on:click={continueAfterMidCheck}
                disabled={midLoading}
              >
                That's right — continue
              </button>
              <button type="button" class="bb-secondary" on:click={goBack} disabled={midLoading}>
                Let me adjust
              </button>
            </div>
          {:else if phase === 'final_readback'}
            <h1 class="bb-q-title">Before I write your brief — is this right?</h1>
            <dl class="bb-final-table">
              {#each readbackRows() as row (row.label)}
                <div class="bb-final-row">
                  <dt>{row.label}</dt>
                  <dd>{row.value || '—'}</dd>
                </div>
              {/each}
            </dl>
            <div class="bb-method-box">
              <p class="bb-readback-label">Recommended methodology</p>
              <p>
                Rapid iterative qualitative testing shaped by your decision, audience, and constraints —
                refined when the brief is written.
              </p>
            </div>
            <div class="bb-actions">
              <button type="button" class="bb-primary" on:click={startGeneration} disabled={saving}>
                {guest ? 'Sign up to generate' : 'Write my brief'}
              </button>
              <button type="button" class="bb-secondary" on:click={goBack}>Let me adjust</button>
            </div>
          {:else if currentStep}
            <p class="bb-eyebrow">Question {stepIndex + 1} of {steps.length}</p>
            <h1 class="bb-q-title">{currentStep.prompt}</h1>
            {#if currentStep.example}
              <p class="bb-example">For example — {currentStep.example}</p>
            {/if}
            {#if currentStep.hint}
              <p class="bb-example">{currentStep.hint}</p>
            {/if}
            {#if currentStep.encouragement}
              <p class="bb-coach">{currentStep.encouragement}</p>
            {/if}

            {#if currentStep.kind === 'text'}
              <div class="bb-input-wrap">
                <label class="sr-only" for="bb-answer">Your answer</label>
                <textarea
                  id="bb-answer"
                  class="bb-textarea"
                  rows="4"
                  placeholder={currentStep.placeholder || 'Type your answer…'}
                  bind:value={draft}
                  on:input={() => {
                    applyDraftToAnswers();
                    persistGuest();
                  }}
                ></textarea>
              </div>
              {#if currentStep.addon}
                <label class="bb-addon-label" for="bb-addon">{currentStep.addon.label}</label>
                <textarea
                  id="bb-addon"
                  class="bb-textarea"
                  rows="3"
                  placeholder={currentStep.addon.placeholder}
                  bind:value={answers[currentStep.addon.key]}
                  on:input={() => {
                    answers = answers;
                    persistGuest();
                  }}
                ></textarea>
              {/if}
            {:else if currentStep.kind === 'territory'}
              <div class="bb-territory-grid" role="listbox" aria-label="What this is about">
                {#each TERRITORIES as t (t.id)}
                  <button
                    type="button"
                    class="bb-territory-card"
                    class:selected={answers.territory === t.id}
                    role="option"
                    aria-selected={answers.territory === t.id}
                    on:click={() => selectTerritory(t.id)}
                  >
                    <span class="bb-territory-title">{t.title}</span>
                  </button>
                {/each}
              </div>
            {:else if currentStep.kind === 'choice'}
              <div class="bb-choice-grid" role="radiogroup" aria-label={currentStep.prompt}>
                {#each currentStep.choices || [] as option (option.value)}
                  <button
                    type="button"
                    class="bb-choice-card"
                    class:selected={answers.ideas === option.value}
                    role="radio"
                    aria-checked={answers.ideas === option.value}
                    on:click={() => {
                      answers.ideas = option.value;
                      if (option.value !== 'I have a few ideas') answers.ideasDetail = '';
                      answers = answers;
                    }}
                  >
                    <strong>{option.label}</strong>
                    <span>{option.hint}</span>
                  </button>
                {/each}
              </div>
              {#if answers.ideas === 'I have a few ideas'}
                <label class="bb-addon-label" for="bb-ideas">What are they?</label>
                <textarea
                  id="bb-ideas"
                  class="bb-textarea"
                  rows="3"
                  placeholder="The routes you're already considering"
                  bind:value={answers.ideasDetail}
                  on:input={() => (answers = answers)}
                ></textarea>
              {/if}
            {:else if currentStep.kind === 'when'}
              <textarea
                class="bb-textarea"
                rows="3"
                placeholder={currentStep.placeholder}
                bind:value={answers.practicalities.decisionDate}
                on:input={() => (answers = answers)}
              ></textarea>
              <p class="bb-addon-label" id="bb-evidence-label">How solid does the evidence need to be? (optional)</p>
              <div class="bb-evidence" role="group" aria-labelledby="bb-evidence-label">
                {#each evidenceOptions as option (option.id)}
                  <button
                    type="button"
                    class="bb-choice-card"
                    class:selected={answers.practicalities.solidity === option.id}
                    aria-pressed={answers.practicalities.solidity === option.id}
                    on:click={() => toggleEvidence(option.id)}
                  >
                    <strong>{option.title}</strong>
                    <span>{option.hint}</span>
                  </button>
                {/each}
              </div>
              <label class="bb-addon-label" for="bb-locked">
                Is anything already locked or under way — media booked, agency work started, budget set? (optional)
              </label>
              <textarea
                id="bb-locked"
                class="bb-textarea"
                rows="3"
                placeholder="e.g. Media is booked; our agency is developing two routes"
                bind:value={answers.practicalities.locked}
                on:input={() => (answers = answers)}
              ></textarea>
            {/if}

            <div class="bb-nav">
              {#if stepIndex > 0}
                <button type="button" class="bb-back" on:click={goBack}>← Back</button>
              {:else}
                <span></span>
              {/if}
              <div class="bb-nav-right">
                {#if currentStep.optional}
                  <button type="button" class="bb-back" on:click={skipOptional}>Skip</button>
                {/if}
                <button
                  type="button"
                  class="bb-primary"
                  disabled={currentStep.kind === 'text'
                    ? !canContinueText
                    : currentStep.kind === 'territory'
                      ? !answers.territory
                      : currentStep.kind === 'choice'
                        ? !choiceReady
                        : currentStep.kind === 'when'
                          ? !answers.practicalities.decisionDate.trim()
                          : false}
                  on:click={goNext}
                >
                  {stepIndex >= steps.length - 1 ? 'Finish' : 'Continue'}
                </button>
              </div>
            </div>
          {/if}
        </div>
      </main>

      <BriefBuilderSidebar
        {answers}
        currentStepId={phase === 'question' ? currentStep?.id ?? null : null}
        liveDraft={phase === 'question' ? liveDraft : ''}
        showTimer={false}
      />
    </div>
  {/if}
</div>

<style>
  /* Replit interaction model: column shell → topbar + (question | sidebar) row */
  .bb-shell {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 100vh;
    background: var(--dashbg);
    color: var(--grey);
    overflow: hidden;
    text-align: left;
  }

  .bb-topbar {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-4);
    padding: var(--space-4) var(--space-6);
    z-index: 5;
  }

  .bb-topbar-spacer {
    width: 1px;
    height: 1px;
  }

  .bb-exit {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    background: var(--surface);
    color: var(--grey);
    font-size: var(--text-sm);
    font-weight: 500;
    cursor: pointer;
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-input);
    border: 1px solid var(--border-color);
    transition: background-color 0.15s ease, border-color 0.15s ease;
  }

  .bb-exit:hover {
    background: var(--surface-hover);
  }

  .bb-exit:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }

  .bb-timer {
    font-size: var(--text-xs);
    font-weight: 500;
    color: var(--accent-orange-ink);
    font-variant-numeric: tabular-nums;
    text-align: left;
  }

  .bb-timer.over {
    color: var(--status-danger-ink);
  }

  .bb-stage {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    padding: var(--space-6) clamp(1.5rem, 6vw, 5rem);
    text-align: left;
  }

  .bb-stage-center {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .bb-body {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    justify-content: flex-start;
    text-align: left;
  }

  .bb-intro {
    width: min(40rem, 92vw);
    text-align: left;
  }

  /* Wide question column — content left-aligned, not edge-pinned right */
  .bb-main {
    flex: 1 1 auto;
    min-width: 0;
    overflow: auto;
    padding: var(--space-6) clamp(1.5rem, 5vw, 4rem) var(--space-8);
    text-align: left;
  }

  /* Same as Replit: max-w-3xl w-full mx-auto — sits in the left column, text left-aligned */
  .bb-main-inner {
    width: 100%;
    max-width: 48rem;
    margin: 0 auto;
    text-align: left;
  }

  .bb-generating {
    width: min(40rem, 100%);
    margin: 12vh auto 0;
    text-align: center;
  }

  /* Narrow assembling column — text starts at the divider (left), never flush viewport-right */
  .bb-body :global(.bb-sidebar) {
    flex: 0 0 clamp(16rem, 28vw, 22rem);
    width: clamp(16rem, 28vw, 22rem);
    border-left: 1px solid var(--border-color);
    text-align: left;
    align-self: stretch;
  }

  .bb-eyebrow {
    margin: 0 0 var(--space-3);
    font-size: var(--text-xs);
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--accent-orange-ink);
    text-align: left;
  }

  .bb-intro-title,
  .bb-q-title {
    margin: 0 0 var(--space-4);
    font-size: clamp(1.85rem, 3.2vw, 2.75rem);
    font-weight: 500;
    line-height: 1.2;
    color: var(--grey);
    text-wrap: balance;
    text-align: left;
  }

  .bb-intro-title em {
    font-style: italic;
    color: var(--accent-orange-ink);
    font-weight: 500;
  }

  .bb-intro-body,
  .bb-example {
    margin: 0.35rem 0 0;
    color: var(--grey-secondary);
    font-size: 0.95rem;
    font-style: italic;
    line-height: 1.45;
  }

  .bb-coach {
    display: inline-flex;
    margin: 0.85rem 0 1.15rem;
    padding: 0.45rem 0.85rem;
    border-radius: 999px;
    background: color-mix(in srgb, #45c496 18%, transparent);
    color: #1f7a5c;
    font-size: 0.95rem;
  }

  .bb-addon-label {
    display: block;
    margin: 1.25rem 0 0.4rem;
    font-weight: 500;
    color: var(--grey);
  }

  .bb-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-top: 1.5rem;
  }

  .bb-nav-right {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .bb-choice-grid,
  .bb-evidence {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 0.75rem;
    margin-top: 1rem;
  }

  .bb-choice-card {
    text-align: left;
    border: 1px solid var(--border-color);
    border-radius: 12px;
    background: var(--white);
    padding: 0.9rem 1rem;
    cursor: pointer;
  }

  .bb-choice-card strong,
  .bb-choice-card span {
    display: block;
  }

  .bb-choice-card span {
    margin-top: 0.25rem;
    color: var(--grey-secondary);
    font-size: 0.9rem;
  }

  .bb-choice-card.selected {
    background: #45c496;
    border-color: #45c496;
    color: white;
  }

  .bb-choice-card.selected span {
    color: white;
  }

  .bb-wait-dots {
    display: flex;
    gap: 0.45rem;
    justify-content: center;
    margin-bottom: 1.25rem;
  }

  .bb-wait-dots span {
    width: 0.55rem;
    height: 0.55rem;
    border-radius: 999px;
    background: #45c496;
    animation: bb-wait 1.1s infinite ease-in-out;
  }

  .bb-wait-dots span:nth-child(2) { animation-delay: 0.15s; }
  .bb-wait-dots span:nth-child(3) { animation-delay: 0.3s; }

  @keyframes bb-wait {
    0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
    40% { opacity: 1; transform: translateY(-3px); }
  }

  .bb-q-hint {
    margin: 0 0 var(--space-5);
    color: var(--grey-secondary);
    font-size: var(--text-base);
    line-height: 1.55;
    max-width: 42rem;
    text-align: left;
  }

  .bb-tip {
    margin: 0 0 var(--space-6);
    padding: 0 0 0 var(--space-4);
    border-left: 3px solid var(--accent-orange);
    color: var(--grey-secondary);
    font-size: var(--text-sm);
    line-height: 1.5;
  }

  .bb-metrics {
    display: flex;
    gap: var(--space-6);
    margin-bottom: var(--space-6);
    padding: var(--space-4) 0;
  }

  .bb-metric {
    display: flex;
    flex-direction: column;
    padding-right: var(--space-6);
    border-right: 1px solid var(--border-color);
  }

  .bb-metric:last-child {
    border-right: 0;
  }

  .bb-metric-num {
    font-size: var(--text-2xl);
    font-weight: 500;
    font-variant-numeric: tabular-nums;
  }

  .bb-metric-label {
    font-size: var(--text-xs);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--grey-secondary);
  }

  .bb-primary {
    border: 0;
    cursor: pointer;
    padding: var(--space-3) var(--space-5);
    border-radius: var(--radius-cta);
    background: var(--primary);
    color: var(--white);
    font-weight: 500;
    font-size: var(--text-base);
    box-shadow: var(--shadow-cta);
    transition: background-color 0.15s ease, opacity 0.15s ease, box-shadow 0.15s ease;
  }

  .bb-primary:hover:not(:disabled) {
    background: var(--darkprimary);
  }

  .bb-primary:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    box-shadow: none;
  }

  .bb-primary:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }

  .bb-secondary {
    border: 1px solid var(--border-strong);
    background: transparent;
    color: var(--grey);
    cursor: pointer;
    padding: var(--space-3) var(--space-5);
    border-radius: var(--radius-cta);
    font-weight: 500;
    transition: border-color 0.15s ease, color 0.15s ease, background-color 0.15s ease;
  }

  .bb-secondary:hover {
    border-color: var(--primary);
    color: var(--primary);
    background: var(--surface);
  }

  .bb-secondary:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }

  .bb-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-4);
    margin-top: var(--space-5);
  }

  .bb-textarea {
    width: 100%;
    box-sizing: border-box;
    padding: var(--space-3) var(--space-4);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-input);
    background: var(--surface);
    font: inherit;
    color: var(--grey);
  }

  .bb-textarea:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }

  .bb-territory-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: var(--space-3);
  }

  .bb-territory-card {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-2);
    text-align: left;
    padding: var(--space-4);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-card);
    background: var(--surface);
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .bb-territory-card:hover {
    border-color: var(--secondary);
  }

  .bb-territory-card.selected {
    border-color: var(--primary);
    background: color-mix(in srgb, var(--primary) 5%, var(--surface));
    box-shadow: var(--shadow-card);
  }

  .bb-territory-card:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }

  .bb-territory-title {
    font-weight: 500;
    color: var(--grey);
  }

  .bb-readback-card,
  .bb-method-box {
    background: var(--surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-card);
    padding: var(--space-5);
    margin-bottom: var(--space-5);
  }

  .bb-method-box {
    background: var(--accent-orange-soft);
    border-color: transparent;
  }

  .bb-readback-loading {
    color: var(--grey-secondary);
    font-style: italic;
  }

  .bb-mid-flags {
    list-style: none;
    margin: 0 0 var(--space-5);
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .bb-mid-flags li {
    display: flex;
    align-items: flex-start;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-4);
    background: var(--status-warn-soft);
    color: var(--status-warn-ink);
    border-radius: var(--radius-sm);
    font-size: var(--text-sm);
    line-height: 1.45;
    text-align: left;
  }

  .bb-mid-flags li :global(i.icon) {
    margin: 0.15em 0 0;
    opacity: 0.85;
  }

  .bb-mid-note {
    margin: 0 0 var(--space-4);
    font-size: var(--text-sm);
    color: var(--grey-secondary);
  }

  .bb-readback-label {
    margin: 0 0 var(--space-2);
    font-size: var(--text-xs);
    font-weight: 500;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--grey-secondary);
  }

  .bb-readback-text {
    margin: 0;
    font-size: var(--text-lg);
    line-height: 1.5;
  }

  .bb-readback-text :global(mark) {
    background: var(--accent-orange-soft);
    color: inherit;
    padding: 0 0.15em;
  }

  .bb-final-table {
    margin: 0 0 var(--space-5);
  }

  .bb-final-row {
    display: grid;
    grid-template-columns: 10rem 1fr;
    gap: var(--space-4);
    padding: var(--space-3) 0;
    border-bottom: 1px solid var(--border-color);
  }

  .bb-final-row dt {
    font-size: var(--text-xs);
    font-weight: 500;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--grey-secondary);
  }

  .bb-final-row dd {
    margin: 0;
    font-size: var(--text-sm);
    line-height: 1.45;
  }

  .bb-back {
    margin-top: var(--space-6);
    border: 0;
    background: transparent;
    color: var(--grey-secondary);
    cursor: pointer;
    font-size: var(--text-sm);
    padding: 0;
  }

  .bb-back:hover {
    color: var(--primary);
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }

  @media (max-width: 900px) {
    .bb-body {
      flex-direction: column;
      overflow: auto;
    }

    .bb-main {
      flex: 1 1 auto;
      padding: var(--space-4);
    }

    .bb-body :global(.bb-sidebar) {
      flex: 0 0 auto;
      width: 100%;
      max-width: none;
      min-width: 0;
      border-left: 0;
      border-top: 1px solid var(--border-color);
      max-height: 36vh;
    }
  }
</style>
